import React from "react";
import {
  createBrowserRouter,
  Navigate,
  useParams,
} from "react-router-dom";

// Auth
import LoginPage from "../auth/LoginPage";
import ForgotPasswordPage from "../auth/ForgotPasswordPage";
import ResetPasswordPage from "../auth/ResetPasswordPage";

// Shared
import PrivateLayout from "../auth/PrivateLayout";
import SimplePrivateLayout from "../auth/SimplePrivateLayout";
import SellerPrivateLayout from "../auth/SellerPrivateLayout";
import Profile from "../Pages/Common/Profile";
import MembershipPlans from "../Pages/Common/MembershipPlans";
import BizcoverIndiaNotFoundPage from "../Pages/Common/BizcoverIndiaNotFoundPage";
import { SHOW_BUYER_ANALYTICS, SHOW_SELLER_ANALYTICS } from "@b2b/ui-components";

// Buyer
import BuyerDashboard from "../Pages/Buyer/BuyerDashboard";
import BuyerAnalytics from "../Pages/Buyer/BuyerAnalytics";
import RfqListPage from "../Pages/Buyer/RfqListPage";
import RfqCreatePage from "../Pages/Buyer/RfqCreatePage";
import RfqEditPage from "../Pages/Buyer/RfqEditPage";
import RFQDashboard from "../Pages/Buyer/RFQDashboard";
import RFQDetail from "../Pages/Buyer/RFQDetail";
import RFQ_QuoteTable from "../Pages/Buyer/RFQ_QuoteTable";
import FavoritesPage from "../Pages/Buyer/FavoritesPage";
import BuyerEnquiriesPage from "../Pages/Buyer/BuyerEnquiriesPage";

// Seller
import SellerAnalyticsWrapper from "../Pages/Seller/SellerAnalyticsPage";
import SellerDashboardWrapper from "../Pages/Seller/SellerDashboardPage";
import SellerRfqInbox from "../Pages/Seller/SellerRfqInbox";
import RFQ_QuoteSubmission from "../Pages/Seller/RFQ_QuoteSubmission";
import RFQ_QuoteTablePage from "@b2b/ui-components/features/RFQ_QuoteTable/RFQ_QuoteTablePage";
import ProductManagement from "../Pages/Admin/AdminProducts";
import StorefrontManagement from "../Pages/Seller/StorefrontManagement";
import SellerProductDashboard from "../Pages/Seller/ProductDashboard";
import SellerProductSearch from "../Pages/Seller/ProductSearch";
import SellerProductList from "../Pages/Seller/ProductList";
import SellerEnquiriesPage from "../Pages/Seller/SellerEnquiriesPage";

// Admin
import Userprofile from "../Pages/Admin/Userprofile";
import ProductList from "../Pages/Admin/ProductList";
import ProductDashboard from "../Pages/Admin/ProductDashboard";
import AdminProductSearch from "../Pages/Admin/ProductSearch";
import AdminProductList from "../Pages/Admin/ProductList";
import UserDashboard from "../Pages/Admin/UserDashboard";
import UserSearch from "../Pages/Admin/UserSearch";
import UserList from "../Pages/Admin/UserList";

// Common (Shared between Admin and Seller)
import CreateProductPage from "../Pages/Common/CreateProductPage";

// Public Pages
import Categories from "../Pages/Common/Categories";
import CategoryDetail from "../Pages/Common/CategoryDetail";
import ProductSearch from "../Pages/Common/ProductSearch";
import NewProductSearch from "../Pages/Common/NewProductSearch";
import CategoryProductList from "../Pages/Common/ProductList";
import SectionProductListPage from "../Pages/Common/SectionProductListPage";
import BannerProductListPage from "../Pages/Common/BannerProductListPage";
import BizcoverIndiaUpdatedHomePage from "../Pages/Common/BizcoverIndiaUpdatedHomePage";
import BizcoverIndiaProductDetailPage from "../Pages/Common/BizcoverIndiaProductDetailPage";
import BizcoverIndiaSellerListPage from "../Pages/Common/BizcoverIndiaSellerListPage";
import BizcoverIndiaSellerDetailPage from "../Pages/Common/BizcoverIndiaSellerDetailPage";
import AdminDashboard from "../Pages/Admin/AdminDashboard";
import AdminUserList from "../Pages/Admin/AdminUserList";
import AdminProducts from "../Pages/Admin/AdminProducts";
import AdminTransactions from "../Pages/Admin/AdminTransactions";
import AdminContent from "../Pages/Admin/AdminContent";
import AdminSettings from "../Pages/Admin/AdminSettings";
import MediaManagement from "../Pages/Admin/MediaManagement";
import AdminReports from "../Pages/Admin/AdminReports";
import AdminRfqReports from "../Pages/Admin/AdminRfqReports";
import AdminMembership from "../Pages/AdminMembership";
import AdminCategories from "../Pages/Admin/AdminCategories";
import AdminRFQManagement from "../Pages/Admin/AdminRFQManagement";
import AdminChatMonitoring from "../Pages/Admin/AdminChatMonitoring";
import AdminEnquiriesPage from "../Pages/Admin/AdminEnquiriesPage";
import AdminHomePage from "../Pages/Admin/AdminHomePage";

// Utilities
import ScrollToTopWrapper from "@b2b/ui-components/components/ScrollToTopWrapper";
import AppRouteShell from "./AppRouteShell";
import SupplierListPage from "../Pages/SupplierListPage";
import SubAccountManagementPage from "../Pages/Admin/SubAccountManagement";
import ProfileSettingsPage from "../Pages/ProfileSettingsPage";
import UnauthorizedPage from "../Pages/Common/UnauthorizedPage";
import BizcoverIndiaFigmaHeaderPreview from "../Pages/Common/BizcoverIndiaFigmaHeaderPreview";
import BizcoverIndiaFigmaFooterPreview from "../Pages/Common/BizcoverIndiaFigmaFooterPreview";
import ChatPage from "../Pages/Common/Chat";
import { SellerChatGuard } from "../auth/SellerChatGuard";

/** Legacy URL: /categories/explore/:id → product listing by category id */
function CategoryExploreRedirect() {
  const { categoryId } = useParams<{ categoryId: string }>();
  if (!categoryId) {
    return <Navigate to="/categories" replace />;
  }
  return <Navigate to={`/products/category/${categoryId}`} replace />;
}

/** Legacy URL: /seller_detail/:sellerId → /seller/:sellerId */
function SellerDetailRedirect() {
  const { sellerId } = useParams<{ sellerId: string }>();
  if (!sellerId) {
    return <Navigate to="/sellers" replace />;
  }
  return <Navigate to={`/seller/${sellerId}`} replace />;
}

/** Legacy URL: /bizcover/product/:id → /product/:id */
function BizcoverProductDetailRedirect() {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return <Navigate to="/" replace />;
  }
  return <Navigate to={`/product/${id}`} replace />;
}

/** Legacy URL: /bizcover/seller/:sellerId → /seller/:sellerId */
function BizcoverSellerDetailRedirect() {
  const { sellerId } = useParams<{ sellerId: string }>();
  if (!sellerId) {
    return <Navigate to="/sellers" replace />;
  }
  return <Navigate to={`/seller/${sellerId}`} replace />;
}

export const router = createBrowserRouter([
  {
    element: <AppRouteShell />,
    children: [
  // 🔹 Auth
  { path: "/login", element: <LoginPage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  {
    path: "/registration",
    element: <Navigate to="/login?tab=signup" replace />,
  },

  // 🔹 Public Layout (guest accessible)
  {
    element: <ScrollToTopWrapper />,
    children: [
      { path: "/", element: <BizcoverIndiaUpdatedHomePage /> }, // Home page at root
      { path: "/home", element: <BizcoverIndiaUpdatedHomePage /> },
      { path: "/categories", element: <Categories /> },
      { path: "/categories/:categorySlug", element: <CategoryDetail /> },
      {
        path: "/categories/explore/:categoryId",
        element: <CategoryExploreRedirect />,
      },
      {
        path: "/products/category/:categoryId",
        element: <CategoryProductList />,
      },
      { path: "/search", element: <NewProductSearch /> },
      { path: "/search/:subcategory", element: <NewProductSearch /> },
      { path: "/section/:sectionKey", element: <SectionProductListPage /> },
      { path: "/banner/:bannerKey", element: <BannerProductListPage /> },
      { path: "/product/:id", element: <BizcoverIndiaProductDetailPage /> },
      { path: "/bizcover/product/:id", element: <BizcoverProductDetailRedirect /> },
      { path: "/sellers", element: <BizcoverIndiaSellerListPage /> },
      { path: "/bizcover/sellers", element: <Navigate to="/sellers" replace /> },
      { path: "/seller/:sellerId", element: <BizcoverIndiaSellerDetailPage /> },
      { path: "/bizcover/seller/:sellerId", element: <BizcoverSellerDetailRedirect /> },
      { path: "/supplier", element: <SupplierListPage /> },
      { path: "/pricing", element: <MembershipPlans /> },
      { path: "/membership-plans", element: <MembershipPlans /> },
      { path: "/unauthorized", element: <UnauthorizedPage /> },
      { path: "/figma-header-preview", element: <BizcoverIndiaFigmaHeaderPreview /> },
      { path: "/figma-footer-preview", element: <BizcoverIndiaFigmaFooterPreview /> },
      { path: "/bizcoverindia-home-updated", element: <Navigate to="/" replace /> },
    ],
  },

  // 🔹 Buyer Private Layout
  {
    path: "/buyer",
    element: <PrivateLayout role="buyer" />, // role-based guard
    children: [
      { path: "dashboard", element: <BuyerDashboard /> },
      {
        path: "analytics",
        element: SHOW_BUYER_ANALYTICS ? (
          <BuyerAnalytics />
        ) : (
          <Navigate to="/buyer/dashboard" replace />
        ),
      },
      { path: "rfq_list", element: <RfqListPage /> },
      { path: "rfq_create_form", element: <RfqCreatePage /> },
      { path: "rfq/:id/edit", element: <RfqEditPage /> },
      { path: "rfq_dashboard", element: <RFQDashboard /> },
      { path: "rfq/:id", element: <RFQDetail /> },
      { path: "rfq_quoteTable", element: <RFQ_QuoteTable /> },
      { path: "favorites", element: <FavoritesPage /> },
      { path: "enquiries", element: <BuyerEnquiriesPage /> },
      { path: "profile", element: <Profile /> },
      { path: "membership_plans", element: <MembershipPlans /> },
      { path: "profile_settings", element: <ProfileSettingsPage /> },
    ],
  },

  // 🔹 Legacy seller profile URL -> new public seller detail URL
  {
    path: "/seller_detail/:sellerId",
    element: <SellerDetailRedirect />,
  },

  // 🔹 Buyer/Seller Chat (Alibaba-style DM). Sellers need profile complete to access.
  {
    path: "/chat",
    element: <SimplePrivateLayout allowedRoles={["buyer", "seller"]} />,
    children: [
      {
        path: "",
        element: (
          <SellerChatGuard>
            <ChatPage />
          </SellerChatGuard>
        ),
      },
    ],
  },

  // 🔹 Seller Private Layout (with SellerStatusGuard)
  {
    path: "/seller",
    element: <SellerPrivateLayout />,
    children: [
      { path: "dashboard", element: <SellerDashboardWrapper /> },
      {
        path: "analytics",
        element: SHOW_SELLER_ANALYTICS ? (
          <SellerAnalyticsWrapper />
        ) : (
          <Navigate to="/seller/dashboard" replace />
        ),
      },
      { path: "enquiries", element: <SellerEnquiriesPage /> },
      { path: "rfq-inbox", element: <SellerRfqInbox /> },
      { path: "rfq/:id/quote", element: <RFQ_QuoteSubmission /> },
      { path: "quotes", element: <RFQ_QuoteTablePage /> },
      { path: "store_front", element: <StorefrontManagement /> },
      // New product management routes
      { path: "products/dashboard", element: <SellerProductDashboard /> },
      { path: "products/search", element: <SellerProductSearch /> },
      { path: "products/list", element: <SellerProductList /> },
      // Backward compatibility redirects
      {
        path: "product_management",
        element: <Navigate to="/seller/products/dashboard" replace />,
      },
      {
        path: "products",
        element: <Navigate to="/seller/products/dashboard" replace />,
      },
      { path: "create_product", element: <CreateProductPage /> },
      { path: "create_product/:id", element: <CreateProductPage /> },
      { path: "media_management", element: <MediaManagement role="seller" /> },
      { path: "profile", element: <Profile /> },
      { path: "membership_plans", element: <MembershipPlans /> },
      { path: "profile_settings", element: <ProfileSettingsPage /> },
    ],
  },

  // 🔹 Admin Private Layout
  {
    path: "/admin",
    element: <PrivateLayout role="admin" />,
    children: [
      { path: "userprofile/:id", element: <ProfileSettingsPage /> },
      { path: "product_list", element: <ProductList /> },
      {
        path: "profile",
        element: <Navigate to="/admin/profile_settings" replace />,
      },
      { path: "membership_plans", element: <MembershipPlans /> },
      { path: "dashboard", element: <AdminDashboard /> },
      // New user management routes
      { path: "users/dashboard", element: <UserDashboard /> },
      { path: "users/search", element: <UserSearch /> },
      { path: "users/list", element: <UserList /> },
      // Backward compatibility redirects
      {
        path: "admin_userlist",
        element: <Navigate to="/admin/users/dashboard" replace />,
      },
      // New product management routes
      { path: "products/dashboard", element: <ProductDashboard /> },
      { path: "products/search", element: <AdminProductSearch /> },
      { path: "products/list", element: <AdminProductList /> },
      // Backward compatibility redirects
      {
        path: "admin_products",
        element: <Navigate to="/admin/products/dashboard" replace />,
      },
      { path: "create_product", element: <CreateProductPage /> },
      { path: "create_product/:id", element: <CreateProductPage /> },
      { path: "admin_transactions", element: <AdminTransactions /> },
      { path: "admin_content", element: <AdminContent /> },
      { path: "homepage", element: <AdminHomePage /> },
      { path: "media_management", element: <MediaManagement /> },
      { path: "admin_settings", element: <AdminSettings /> },
      { path: "admin_reports", element: <AdminReports /> },
      { path: "admin_rfq_reports", element: <AdminRfqReports /> },
      { path: "admin_membership_plans", element: <AdminMembership /> },
      { path: "admin_categories", element: <AdminCategories /> },
      { path: "admin_rfq", element: <AdminRFQManagement /> },
      { path: "admin_chat", element: <AdminChatMonitoring /> },
      { path: "enquiries", element: <AdminEnquiriesPage /> },
      { path: "profile_settings", element: <ProfileSettingsPage /> },
      // Legacy redirect: /admin/profile redirects to /admin/profile_settings
      { path: "sub_account_management", element: <SubAccountManagementPage /> },
    ],
  },

  // 🔹 Fallback
  { path: "*", element: <BizcoverIndiaNotFoundPage /> },
    ],
  },
]);
